// Footer.js
import React, { useState, useEffect, memo } from "react"; // memo import kiya
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Divider,
  Grid,
  Typography,
  Link as MuiLink,
} from "@mui/material";

import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import { LinkedIn } from "@mui/icons-material";

import API from "../utils/API";

function Footer() {
  const [data, setData] = useState({});
  const [pages, setPages] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyProfileRes, pagesRes] = await Promise.all([
          API({ method: "GET", url: "company_profile/" }),
          API({ method: "GET", url: "page/" }),
        ]);

        if (companyProfileRes && companyProfileRes.length > 0) {
          setData(companyProfileRes[0]);
        }
        setPages(pagesRes);
      } catch (error) {
        console.error("Error fetching footer data:", error);
      }
    };
    fetchData();
  }, []); // To run the API calls only once, keep the dependencies array empty []

  // Memoized footerHtmlContent using useMemo to avoid unnecessary re-calculation
  const footerHtmlContent = React.useMemo(() => {
    return `<span>${data.footer || ""}</span>`;
  }, [data.footer]); // data.footer change hone par hi re-calculate hoga

  // Memoized the containsPageWithWeb function using useCallback
  const containsPageWithWeb = React.useCallback((pagesArray, searchName) => {
    return pagesArray.some(
      (page) => page.name && page.name.includes(searchName) && page.web === true
    );
  }, []);

  return (
    <Container
      component="footer"
      maxWidth={false}
      sx={{
        backgroundColor: "#222529",
        color: "#fff",
        pt: { xs: 4, md: 6 },
        pb: { xs: 0.5, md: 0.5 },
        px: { xs: 2, sm: 3, md: 5 },
        position: "relative",
      }}
    >
      <Grid
        container
        spacing={{ xs: 2, sm: 3, md: 1 }}
        justifyContent="space-around"
        alignItems="flex-start"
      >
        <Grid
          item
          xs={6}
          sm={4}
          md={3}
          order={{ xs: 1, md: 1 }}
          sx={{
            ml: { md: 16 },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: { xs: 1, sm: 2 },
              color: "#e0e0e0",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            Links
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              "& a": {
                mb: { xs: 0.5, sm: 1 },
                "& .MuiLink-root": {
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  transition: "color 0.3s ease-in-out",
                  "&:hover": {
                    color: "#2C3C93",
                    textDecoration: "underline",
                  },
                },
              },
            }}
          >
            <Link to={"/branches"}>
              <MuiLink>Our Branches</MuiLink>
            </Link>
            <Link to={"/fastorder"}>
              <MuiLink>Fast Order</MuiLink>
            </Link>
            <Link to={"/Feedback"}>
              <MuiLink>Feedback</MuiLink>
            </Link>
            <Link to={"/faq"}>
              <MuiLink>FAQ's</MuiLink>
            </Link>
          </Box>
        </Grid>

        <Grid item xs={6} sm={4} md={3} order={{ xs: 2, md: 2 }}>
          <Typography
            variant="h5"
            sx={{
              mb: { xs: 1, sm: 2 },
              color: "#e0e0e0",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            About
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              "& a": {
                mb: { xs: 0.5, sm: 1 },
                "& .MuiLink-root": {
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  transition: "color 0.3s ease-in-out",
                  "&:hover": {
                    color: "#2C3C93",
                    textDecoration: "underline",
                  },
                },
              },
            }}
          >
            <Link to={"/franchise"}>
              <MuiLink>Franchise</MuiLink>
            </Link>
            <Link to={"/careers"}>
              <MuiLink>Careers</MuiLink>
            </Link>
            <Link to={"/our-team"} onClick={() => window.scrollTo(0, 0)}>
              <MuiLink>Our Team</MuiLink>
            </Link>
            {containsPageWithWeb(pages, "Privacy Policy") && (
              <Link to={"/privacy-policy"}>
                <MuiLink>Privacy Policy</MuiLink>
              </Link>
            )}
            <Link to={"/our-history"}>
              <MuiLink>Our History</MuiLink>
            </Link>
            {containsPageWithWeb(pages, "Security Policy") && (
              <Link to={"/security-policy"}>
                <MuiLink>Security Policy</MuiLink>
              </Link>
            )}
            {containsPageWithWeb(pages, "Shipping & Returns") && (
              <Link to={"/shipping-and-returns"}>
                <MuiLink>Shipping & Returns</MuiLink>
              </Link>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={3} order={{ xs: 3, md: 3 }}>
          <Typography
            variant="h5"
            sx={{
              mb: { xs: 1, sm: 2 },
              color: "#e0e0e0",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            Contact Us
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: { xs: 0.5, sm: 1 },
              "& .MuiTypography-root": {
                fontSize: { xs: "0.85rem", sm: "1rem" },
              },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "1.2rem", sm: "1.5rem" },
              },
            }}
          >
            <Typography color="#fff" display="flex" alignItems="center">
              <CallRoundedIcon sx={{ mr: 1, color: "#2C3C93" }} />{" "}
              {data.phone_number || "N/A"}
            </Typography>
            <Typography color="#fff" display="flex" alignItems="center">
              <AlternateEmailRoundedIcon sx={{ mr: 1, color: "#2C3C93" }} />{" "}
              {data.email || "N/A"}
            </Typography>
            <Typography color="#fff" display="flex" alignItems="center">
              <LocationOnRoundedIcon sx={{ mr: 1, color: "#2C3C93" }} />{" "}
              {data.address || "N/A"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: 1, sm: 2 },
              mt: { xs: 1, sm: 1.5 },
              mb: { xs: 1, sm: 1.5 },
              pl: 0,
            }}
          >
            <MuiLink
              href="https://play.google.com/store/apps/details?id=com.nmppharmacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                lineHeight: 0,
                ml: { xs: "-4px", sm: "-4px" },
              }}
            >
              {/* Image lazy loading aur size optimization */}
              <img
                alt="Get it on Google Play"
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                style={{
                  width: "120px",
                  height: "auto",
                  maxWidth: "100%",
                  display: "block",
                }}
                loading="lazy"
              />
            </MuiLink>
            <MuiLink
              href="https://apps.apple.com/us/app/nmp-pharmacy/id6560116676"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ lineHeight: 0 }}
            >
              <img
                src="https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/en-us?releaseDate=1721952000"
                alt="Download on the App Store"
                style={{
                  width: "110px",
                  height: "auto",
                  maxWidth: "100%",
                  display: "block",
                }}
                loading="lazy"
              />
            </MuiLink>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mt: { xs: 1, md: 1.5 },
              mb: { xs: 0, sm: 0 },
              pl: 0,
            }}
          >
            {data.facebook_link && (
              <MuiLink
                href={data.facebook_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#fff",
                  mr: { xs: 2, sm: 3 },
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                  "&:hover": { color: "#2C3C93" },
                }}
              >
                <FacebookIcon />
              </MuiLink>
            )}
            {data.instagram_link && (
              <MuiLink
                href={data.instagram_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#fff",
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                  "&:hover": { color: "#C13584" },
                }}
              >
                <InstagramIcon />
              </MuiLink>
            )}
            {data.x_link && (
              <MuiLink
                href={data.x_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#fff",
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                  "&:hover": { color: "#2C3C93" },
                }}
              >
                <TwitterIcon />
              </MuiLink>
            )}
            {data.linkedin_link && (
              <MuiLink
                href={data.linkedin_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#fff",
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                  "&:hover": { color: "#2C3C93" },
                }}
              >
                <LinkedIn />
              </MuiLink>
            )}
          </Box>
        </Grid>
      </Grid>

      <Divider
        sx={{
          mt: 4,
          mb: { xs: 0.5, sm: 0.5 },
          borderColor: "rgba(255,255,255,0.1)",
          animation: "glowingDivider 3s infinite alternate",
          "@keyframes glowingDivider": {
            "0%": {
              boxShadow: "0 0 5px rgba(44, 60, 147, 0.4)",
              borderColor: "rgba(0, 176, 255, 0.4)",
            },
            "50%": {
              boxShadow: "0 0 15px rgba(44, 60, 147, 0.4)",
              borderColor: "rgba(44, 60, 147, 0.4)",
            },
            "100%": {
              boxShadow: "0 0 5px rgba(44, 60, 147, 0.4)",
              borderColor: "rgba(0, 176, 255, 0.4)",
            },
          },
        }}
      />

      <Box sx={{ textAlign: "center", py: { xs: 0.5, sm: 0.5 } }}>
        <Typography
          color="#a0a0a0"
          fontSize={{ xs: 12, sm: 15 }}
          dangerouslySetInnerHTML={{ __html: footerHtmlContent }}
          sx={{
            "& a": {
              color: "#2C3C93",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            },
          }}
        ></Typography>
      </Box>
    </Container>
  );
}
// Wrapping the Footer component with React.memo improves performance by preventing unnecessary re-renders.
export default memo(Footer);
